/**
 * Synchronizes the singleton editor store with a programme platform record.
 *
 * @module platform/editor-sync-context
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { produce } from "immer";

import type { CurriculumChangeCase, Programme as BackendProgramme } from "@/server/types";
import { applyImportedProgramme, writeProgramme } from "@/state/programme-buffer";
import { stampSent, state, useEditorStore } from "@/state/store";
import type { Programme } from "@/types";
import { migrateProgramme } from "@/utils/migrate-programme";

import type { ProgrammePlatformClient } from "./client";
import {
  adoptComposedModules,
  rebaseTypedWork,
  serializeForBaseline,
} from "./editor-sync-baseline";
import {
  type EditorSyncEffect,
  type EditorSyncEvent,
  type EditorSyncState,
  type EditorSyncStatus,
  hasUnsavedEditorChanges,
  holdsUnsavedWork,
  initialEditorSyncState,
  isLeaseConflict,
  isLeaseLapsed,
  isStateConflict,
  isTransientFailure,
  OVERTAKEN_REREADS,
  reduceEditorSync,
} from "./editor-sync-reducer";
import { ModuleEditTarget } from "./module-edit-target";
import { tabClientId } from "./tab-client-id";

export { type EditorSyncStatus, hasUnsavedEditorChanges } from "./editor-sync-reducer";

/**
 * The states in which the server has refused this editor's copy for good.
 *
 * A failed save is not one: it offers Retry, and the copy is still the
 * reader's work, so a re-read another screen asks for keeps it and resends it.
 */
const REFUSED_STATUSES = new Set<EditorSyncStatus>(["conflict", "locked", "unauthorized"]);

/** Unsaved states a send can still clear, as opposed to offline or refused ones. A failed save is one. */
const SETTLEABLE_STATUSES = new Set<EditorSyncStatus>([
  "saved-locally",
  "saving-backend",
  "reconnecting",
  "error",
]);

export interface EditorProgrammeMetadata {
  id: string;
  saveRevision: number;
  visibilityState: BackendProgramme["visibilityState"];
  workflowState: BackendProgramme["workflowState"];
  archivedFrom?: BackendProgramme["archivedFrom"];
  changeCaseId?: string;
  changeCaseKind?: CurriculumChangeCase["kind"];
  baseRevisionId?: string;
  institutionalLabel?: string;
  targetEffectiveFrom?: string;
}

type ReloadOutcome = "read" | "kept" | "failed";
/** A re-read a save overtook: kept, but worth asking again. */
type ReadOutcome = ReloadOutcome | "overtaken";

export interface EditorProgrammeSyncValue {
  record: EditorProgrammeMetadata | null;
  status: EditorSyncStatus;
  error: Error | null;
  /** Why the last background re-read failed, so what is on screen may be behind storage. */
  behind: Error | null;
  /**
   * Whether work on screen has not reached the platform. Wider than the
   * status: typing under a lock or a refused authorization leaves the status
   * on the refusal while the work stays unsent.
   */
  unsaved: boolean;
  openProgramme: (programmeId: string, changeCaseId?: string) => Promise<void>;
  retrySave: () => void;
  /**
   * Read the stored copy again. What came of it shows in the status and in
   * `behind`, for every caller alike. `discardLocal` is the reader choosing
   * the stored copy over refused work.
   */
  reloadProgramme: (options?: { discardLocal?: boolean }) => Promise<void>;
  refreshProgrammeMetadata?: () => Promise<void>;
  closeProgramme: () => Promise<void>;
  /**
   * Send what is unsaved and wait for it. True once nothing is left unsent;
   * false at once while the copy is offline or refused, where a send cannot land.
   */
  settlePendingSave: () => Promise<boolean>;
  /**
   * Take or give up the structural editing lease.
   *
   * Called with `true` on entering a structural step and `false` on leaving
   * the set, not on every step change: one lease across the set, or moving
   * between two structural steps hands another lead a window between them.
   */
  enterStructuralRegion: (active: boolean) => Promise<void>;
  /** Whether the structural lease is held, so the UI can inert what it covers. */
  holdsStructuralLease: boolean;
  /**
   * The client id this editor takes leases under. Presence connects with the
   * same one, or a disconnect has no lease to match.
   */
  clientId: string;
  /** The one write path for the open programme. */
  session: EditSession;
}

/** How an edit leaves: after the session's window, or at once. */
export interface EditOptions {
  save?: "debounced" | "now";
}

/**
 * Writes to the open programme and its save, in one call, so no caller can
 * write without saving. One window per target: edits inside it go as one save.
 */
export interface EditSession {
  edit: (recipe: (draft: Programme) => void, options?: EditOptions) => void;
  /**
   * A peer's change, mirrored without saving it back. Moves the baseline only
   * when nothing of the reader's is unsent; otherwise it rides with their save.
   * Pass merged values: the reader's edit stays pending, but a recipe that
   * writes over a field they changed loses their text.
   */
  applyRemote: (recipe: (draft: Programme) => void) => void;
  /**
   * An edit inside the window, or work not yet on the platform, read when
   * asked: an edit changes it without a render.
   */
  readonly pending: boolean;
  /** Send everything pending and wait. False when a send cannot land. */
  leave: () => Promise<boolean>;
  /**
   * The module target `key` names, the same one each time it is asked for, so
   * a draft whose save could not land outlives the editor that typed it.
   * `pending` and `leave` answer for every target.
   */
  moduleTarget: (key: string) => ModuleEditTarget;
  /** Drop every module draft unsent: the reader chose to leave them. */
  discardModuleDrafts: () => void;
}

/** Names one module's target: the programme, the change case it is edited in, the module. */
export function moduleTargetKey(
  programmeId: string,
  changeCaseId: string | undefined,
  moduleId: string,
): string {
  return `${programmeId}:${changeCaseId ?? ""}:${moduleId}`;
}

/** The edit window: a typing pause long enough to be one change. */
export const EDIT_WINDOW_MS = 400;

interface EditorProgrammeSyncBoundaryProps {
  value: EditorProgrammeSyncValue;
  children: React.ReactNode;
}

const EditorProgrammeSyncContext = React.createContext<EditorProgrammeSyncValue | null>(null);

function metadataFor(
  programme: BackendProgramme,
  changeCase?: CurriculumChangeCase | null,
): EditorProgrammeMetadata {
  return {
    id: programme.id,
    saveRevision: programme.saveRevision,
    visibilityState: programme.visibilityState,
    workflowState: programme.workflowState,
    ...(programme.archivedFrom ? { archivedFrom: programme.archivedFrom } : {}),
    ...(changeCase
      ? {
          changeCaseId: changeCase.id,
          changeCaseKind: changeCase.kind,
          baseRevisionId: changeCase.baseRevisionId,
          ...(changeCase.institutionalLabel
            ? { institutionalLabel: changeCase.institutionalLabel }
            : {}),
          ...(changeCase.targetEffectiveFrom
            ? { targetEffectiveFrom: changeCase.targetEffectiveFrom }
            : {}),
        }
      : {}),
  };
}

function cloneEditorProgramme(): BackendProgramme["structured"] {
  return JSON.parse(JSON.stringify(state.programme)) as BackendProgramme["structured"];
}

function serializeEditorProgramme(): string {
  return serializeForBaseline(state.programme);
}

/** Whether two sync states put the same on screen: all the render reads of one. */
function showsAlike(a: EditorSyncState, b: EditorSyncState): boolean {
  return (
    a.status === b.status &&
    a.error === b.error &&
    a.holdsStructuralLease === b.holdsStructuralLease &&
    a.behind === b.behind &&
    holdsUnsavedWork(a) === holdsUnsavedWork(b)
  );
}

/**
 * Write into the buffer what a save took from storage.
 *
 * @returns The serialised document the editor is now saved at.
 */
function adoptSavedComposition(
  sent: BackendProgramme["structured"],
  saved: BackendProgramme["structured"],
): string {
  const adopted = adoptComposedModules(sent, saved, state.programme);
  const { ploToMimlos } = adopted;
  if (adopted.modules.size > 0 || ploToMimlos) {
    writeProgramme((draft) => {
      adopted.modules.forEach((module, at) => {
        draft.modules[at] = module;
      });
      if (ploToMimlos) {
        draft.ploToMimlos = ploToMimlos;
      }
    });
  }
  return adopted.baseline;
}

/** The workflow states a programme takes edits in. */
export const EDITABLE_WORKFLOW_STATES = new Set<BackendProgramme["workflowState"] | undefined>([
  "Draft",
  "Ready for Development",
  "In Development",
  "Changes Required",
]);

export function useEditorProgrammeSyncController(
  client: ProgrammePlatformClient,
  syncDelayMs = 100,
  editLeasesEnabled = true,
  editWindowMs = EDIT_WINDOW_MS,
): EditorProgrammeSyncValue {
  const [record, setRecord] = useState<EditorProgrammeMetadata | null>(null);
  const [initialSync] = useState(() => initialEditorSyncState(window.navigator.onLine));
  const [sync, setSync] = useState(initialSync);
  // Read by a re-read, which has to know whether the editor's own copy was
  // still savable when it started. Written with the state rather than after
  // the render, so a re-read in the same tick as a refusal sees the refusal.
  const syncRef = useRef(initialSync);
  // Set by the save effect, which owns the debounce the reducer schedules on.
  const queueSaveRef = useRef<(() => void) | null>(null);
  // A revision conflict re-reads under the typed work and resends it, since a
  // module saved elsewhere moves the revision without touching what was typed.
  const rebaseAfterConflictRef = useRef<((conflict: Error) => void) | null>(null);
  // A lease request that could not go out offline or failed in transit, made
  // once the connection returns or on the next tick; or a lost lease, asked for
  // now. Set by the request itself, whatever asked for it.
  const leaseOwedRef = useRef(false);
  const requestOwedLeaseRef = useRef<(() => void) | null>(null);
  const send = useCallback((event: EditorSyncEvent): readonly EditorSyncEffect[] => {
    const previous = syncRef.current;
    const { state: next, effects } = reduceEditorSync(previous, event);
    syncRef.current = next;
    // The rest is bookkeeping read from the ref, and a keystroke or a lease
    // renewal re-rendering the editor for it is wasted.
    if (!showsAlike(previous, next)) {
      setSync(next);
    }
    effects.forEach((effect) => {
      switch (effect.type) {
        case "schedule-save":
        case "resend":
          queueSaveRef.current?.();
          break;
        case "ask-lease":
          leaseOwedRef.current = true;
          requestOwedLeaseRef.current?.();
          break;
        case "rebase":
          rebaseAfterConflictRef.current?.(effect.conflict);
          break;
        default: {
          const unhandled: never = effect;
          throw new Error(`Unhandled editor sync effect: ${JSON.stringify(unhandled)}`);
        }
      }
    });
    return effects;
  }, []);
  const recordRef = useRef<BackendProgramme | null>(null);
  const changeCaseRef = useRef<CurriculumChangeCase | null>(null);
  const targetProgrammeIdRef = useRef<string | null>(null);
  const targetChangeCaseIdRef = useRef<string | null>(null);
  // State rather than a ref: it is read during render to publish on the value,
  // and a ref read there is a hook-rules violation.
  const [clientId] = useState(tabClientId);
  const baselineRef = useRef("");
  // The session's window: armed by an edit, fired by its end, a "now", a
  // switch or `leave()`.
  const editTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Peer changes mirrored while a save was out, replayed onto the baseline it
  // lands with. Null once the reader has changed anything since that save
  // went: the merged text a mirror carries may hold their typing.
  const remoteDuringSaveRef = useRef<((draft: Programme) => void)[] | null>(null);
  // The store as the save in flight left it, with the recorded changes on.
  const untouchedRef = useRef<string | null>(null);
  // Peer changes mirrored onto a settled copy while a re-read is out, put back
  // over what it brings.
  const remoteDuringReadRef = useRef<((draft: Programme) => void)[] | null>(null);
  const disarmEdit = useCallback((): void => {
    if (editTimerRef.current !== null) {
      clearTimeout(editTimerRef.current);
      editTimerRef.current = null;
    }
  }, []);
  // An edit leaving its window, or work owed a send: the save effect hears of
  // it through here. The store has no save event of its own.
  const editedRef = useRef<() => void>(() => undefined);
  const noteEdited = useCallback((): void => {
    stampSent();
    editedRef.current();
  }, []);
  const fireEdit = useCallback((): void => {
    disarmEdit();
    noteEdited();
  }, [disarmEdit, noteEdited]);
  useEffect(
    () => () => {
      disarmEdit();
    },
    [disarmEdit],
  );
  // Which programme is open. A re-read of the same one leaves it alone, so a
  // save sent during the read still lands.
  const generationRef = useRef(0);
  // Which read is current, so an older read never lands over a newer one.
  const readGenerationRef = useRef(0);
  // Which programme the save out was sent for: one outlasting a switch is not
  // the current programme's to follow.
  const savingGenerationRef = useRef(-1);
  // Moves whenever a save starts or settles, so a read can tell it predates one.
  const saveEpochRef = useRef(0);
  const canEditRef = useRef(false);
  // The lease request out, an acquire or the release leaving the steps: until
  // it settles, the server may refuse a save without a lease for this client's own.
  const acquiringRef = useRef<Promise<void> | null>(null);
  // Lease requests made, so a save can tell whether one crossed it.
  const leaseRequestsRef = useRef(0);
  const inStructuralRegionRef = useRef(false);
  // Read after an await, so work finishing after unmount knows nothing is
  // left to release what it was granted.
  const mountedRef = useRef(false);
  // An open record whose buffer differs from what it is saved at.
  const holdsUnsentEdits = useCallback(
    (): boolean => recordRef.current !== null && serializeEditorProgramme() !== baselineRef.current,
    [],
  );
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // Set by the save effect so the lease release can wait for a pending save.
  const flushRef = useRef<(() => Promise<void>) | null>(null);
  // Before the open programme is dropped: sends its unsent edit; the drain
  // sends typing that lands while it is out. A store matching the baseline
  // sends only while its own save is out, which a revert must follow. Undefined
  // when nothing is sent: awaiting even that reorders a switch and a read.
  const sendTyped = useCallback((): Promise<void> | undefined => {
    disarmEdit();
    const saved = serializeEditorProgramme() === baselineRef.current;
    const ownSaveOut =
      syncRef.current.saveInFlight && savingGenerationRef.current === generationRef.current;
    if (!recordRef.current || (saved && !ownSaveOut)) {
      return undefined;
    }
    noteEdited();
    return (flushRef.current?.() ?? Promise.resolve()).catch(() => undefined);
  }, [disarmEdit, noteEdited]);
  // Which read is resending after a revision conflict. While it is the
  // current read, a save would carry the stale revision again; the read
  // resends whatever was typed meanwhile. Any later read, open or close
  // supersedes it, and the hold with it.
  const rebasingRef = useRef<number | null>(null);
  // Which read is the reader replacing their copy out of a refusal. While it
  // is the current read nothing is sent, and a save already out that fails
  // carried the work they chose to throw away, so it reports nothing and
  // starts no rebase read over theirs.
  const discardingRef = useRef<number | null>(null);
  // The discard a save out is measured against: the one reading, else the latest
  // that replaced the copy, so a save that waited out one knows it was thrown away.
  const discardedAtRef = useRef(0);
  // The latest whose content replaced the reader's copy. One that failed, found
  // nothing, or was overtaken replaced nothing.
  const discardLandedAtRef = useRef(0);
  useEffect(() => {
    let disposed = false;
    let saveTimer: ReturnType<typeof setTimeout> | null = null;
    // What the latest save carried, and how many saves have failed with
    // nothing resending them.
    let sent: string | null = null;
    let failures = 0;

    // Owns the debounce so that neither saver has to reach for the other.
    // flushSave re-arms itself through this on a coalesced save, and queueSave
    // is this plus the callback, which is what stops the two referring to each
    // other in a cycle no declaration order can satisfy.
    const schedule = (run: () => void): void => {
      if (saveTimer) {
        clearTimeout(saveTimer);
      }
      saveTimer = setTimeout(run, syncDelayMs);
    };

    const settle = (): Promise<void> =>
      new Promise((resolve) => {
        setTimeout(resolve, syncDelayMs);
      });

    // A save sent while a lease request is out carries no lease id and is refused
    // against the lease this client is taking or giving back. Before a send it
    // waits only inside the region, whose saves need the lease; after a refusal,
    // `anywhere`. A switch ends the wait once the request it is on has settled.
    const awaitStructuralAcquire = async (generation: number, anywhere = false): Promise<void> => {
      const acquiring = acquiringRef.current;
      if (
        acquiring &&
        (anywhere || inStructuralRegionRef.current) &&
        generationRef.current === generation
      ) {
        await acquiring;
        await awaitStructuralAcquire(generation, anywhere);
      }
    };

    const flushSave = async (): Promise<void> => {
      // Taken before the lease wait, which a switch can outlast: the save goes
      // where it was meant, not to whatever opened meanwhile.
      const activeRecord = recordRef.current;
      const activeChangeCase = changeCaseRef.current;
      if (!activeRecord || disposed) {
        return;
      }
      if (syncRef.current.saveInFlight) {
        // Only what was typed since it went out is owed a send after it.
        if (serializeEditorProgramme() !== sent) {
          send({ type: "save-deferred" });
        }
        return;
      }
      if (
        rebasingRef.current === readGenerationRef.current ||
        discardingRef.current === readGenerationRef.current
      ) {
        return;
      }

      const structured = cloneEditorProgramme();
      const serialized = serializeForBaseline(structured);
      if (serialized === baselineRef.current) {
        return;
      }

      const generation = generationRef.current;
      if (!window.navigator.onLine) {
        send({ type: "offline", unsent: true });
        return;
      }
      // This send carries whatever a pending debounce would have.
      if (saveTimer) {
        clearTimeout(saveTimer);
        saveTimer = null;
      }
      const readsBefore = readGenerationRef.current;
      saveEpochRef.current += 1;
      sent = serialized;
      untouchedRef.current = serialized;
      remoteDuringSaveRef.current = [];
      savingGenerationRef.current = generation;
      send({ type: "save-started" });
      await awaitStructuralAcquire(generation);
      // No second epoch bump; the reload already counts from after this one.
      if (discardedAtRef.current > readsBefore) {
        send({ type: "save-abandoned" });
        return;
      }

      const editLeaseId = syncRef.current.lease?.id;
      // A lease asked for while this is out, or earlier and not waited for, may
      // be what refuses it, or may replace another editor's lease that did.
      const askingLease = acquiringRef.current !== null;
      const leaseRequestsBefore = leaseRequestsRef.current;
      try {
        const savedWorkspace = activeChangeCase
          ? await client.saveChangeCaseWorkspace({
              programmeId: activeRecord.id,
              changeCaseId: activeChangeCase.id,
              structured,
              expectedRevision: activeRecord.saveRevision,
              // As the live branch below: without it a whole-document save
              // over a change case overwrites a module somebody else saved
              // into the working copy.
              moduleBaseHashes: activeRecord.moduleBaseHashes,
            })
          : null;
        const saved =
          savedWorkspace?.programme ??
          (await client.saveProgramme({
            id: activeRecord.id,
            structured,
            expectedRevision: activeRecord.saveRevision,
            // The base each module was read at. A whole-document save without
            // it silently overwrites a module somebody else changed while this
            // copy was open.
            moduleBaseHashes: activeRecord.moduleBaseHashes,
            editLeaseId,
          }));
        if (disposed || generationRef.current !== generation) {
          send({ type: "save-abandoned" });
          return;
        }

        saveEpochRef.current += 1;
        recordRef.current = saved;
        changeCaseRef.current = savedWorkspace?.changeCase ?? null;
        baselineRef.current = adoptSavedComposition(structured, saved.structured);
        const remote = remoteDuringSaveRef.current;
        remoteDuringSaveRef.current = null;
        if (remote && remote.length > 0) {
          const replayed = serializeForBaseline(
            produce(JSON.parse(baselineRef.current) as Programme, (draft) => {
              remote.forEach((recipe) => recipe(draft));
            }),
          );
          // Only when the store is exactly that: any of the reader's own work
          // since stays unsent.
          if (replayed === serializeEditorProgramme()) {
            baselineRef.current = replayed;
          }
        }
        setRecord(metadataFor(saved, changeCaseRef.current));
        send({
          type: "save-succeeded",
          settled: serializeEditorProgramme() === baselineRef.current,
        });
      } catch (cause) {
        const saveError = cause instanceof Error ? cause : new Error("Unable to save programme");
        const racedLeaseRequest = askingLease || leaseRequestsRef.current !== leaseRequestsBefore;
        // A lease a request took, or is giving back, may be what refused this,
        // and would refuse it again: it is reported, and so sent again, only
        // once every request out has settled, still counting as out meanwhile.
        if (racedLeaseRequest && (isLeaseConflict(saveError) || isLeaseLapsed(saveError))) {
          await awaitStructuralAcquire(generation, true);
        }
        if (disposed || generationRef.current !== generation) {
          send({ type: "save-abandoned" });
          return;
        }
        // Out or landed: either way the stored copy replaced what this carried.
        if (discardedAtRef.current > readsBefore) {
          send({ type: "save-abandoned" });
          return;
        }
        saveEpochRef.current += 1;
        const effects = send({ type: "save-failed", error: saveError, racedLeaseRequest });
        // A re-read or a resend sending it again is not a failure the drain stops for.
        if (!effects.some((effect) => effect.type === "rebase" || effect.type === "resend")) {
          failures += 1;
        }
      }
    };

    const queueSave = (): void => {
      schedule(() => {
        flushSave();
      });
    };

    // `flushSave` answers at once while a save is in flight, queueing a coalesced
    // one behind it, which the lease release has to wait for. Bounded in passes,
    // so a failing save cannot hold the lease forever, though a pass whose save
    // waits out a lease request lasts as long as that request.
    const drainSaves = async (): Promise<void> => {
      const failuresBefore = failures;
      // The drain waits out saves in flight; it does not retry a failed one,
      // its own or one it waited out, nor wait on one the connection stops
      // from going out.
      for (let attempt = 0; attempt < 20 && failures === failuresBefore; attempt += 1) {
        // eslint-disable-next-line no-await-in-loop -- Justification: draining is sequential by definition, since each pass has to observe the result of the save the previous one started.
        await flushSave();
        if (failures !== failuresBefore || !window.navigator.onLine) {
          return;
        }
        if (!syncRef.current.saveInFlight && serializeEditorProgramme() === baselineRef.current) {
          return;
        }
        // eslint-disable-next-line no-await-in-loop -- Justification: as above, the wait is what lets the in-flight save finish before the next pass looks.
        await settle();
      }
    };

    flushRef.current = drainSaves;
    queueSaveRef.current = queueSave;

    const handleLocalSave = (): void => {
      send({ type: "edited", online: window.navigator.onLine, unsent: holdsUnsentEdits() });
    };

    const handleOffline = (): void => {
      send({ type: "offline", unsent: holdsUnsentEdits() });
    };

    const handleOnline = (): void => {
      send({ type: "online", unsent: holdsUnsentEdits() });
      requestOwedLeaseRef.current?.();
    };

    editedRef.current = handleLocalSave;
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      disposed = true;
      editedRef.current = () => undefined;
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      if (saveTimer) {
        clearTimeout(saveTimer);
      }
      flushRef.current = null;
      queueSaveRef.current = null;
    };
  }, [client, holdsUnsentEdits, send, syncDelayMs]);

  const { status, error, holdsStructuralLease, behind } = sync;
  const unsaved = holdsUnsavedWork(sync);
  useEffect(() => {
    const renew = (): void => {
      const lease = syncRef.current.lease;
      if (!editLeasesEnabled || !lease) {
        return;
      }
      client
        .renewEditLease({ leaseId: lease.id, clientId })
        .then((renewed) => {
          send({ type: "lease-renewed", leaseId: lease.id, lease: renewed });
        })
        .catch((cause) => {
          const leaseError =
            cause instanceof Error ? cause : new Error("The programme editing lease was lost");
          send({ type: "lease-lost", leaseId: lease.id, error: leaseError });
        });
    };
    // Offline a renewal can only fail, which says nothing about the lease; the
    // connection coming back renews it, or finds it lapsed.
    const interval = setInterval(() => {
      if (window.navigator.onLine) {
        renew();
        requestOwedLeaseRef.current?.();
      }
    }, 30_000);
    window.addEventListener("online", renew);
    return () => {
      clearInterval(interval);
      window.removeEventListener("online", renew);
      const lease = syncRef.current.lease;
      send({ type: "lease-released" });
      if (editLeasesEnabled && lease) {
        client.releaseEditLease({ leaseId: lease.id, clientId }).catch(() => undefined);
      }
    };
  }, [client, clientId, editLeasesEnabled, send]);

  const acquireStructuralLease = useCallback(async (): Promise<void> => {
    const request = async (): Promise<void> => {
      const programmeId = targetProgrammeIdRef.current;
      if (
        !mountedRef.current ||
        !programmeId ||
        !canEditRef.current ||
        syncRef.current.holdsStructuralLease ||
        acquiringRef.current
      ) {
        return;
      }
      // A change case edits its own working copy under its own revision, so it
      // needs no programme lease, and asking for one is refused while anyone
      // else holds the programme.
      if (targetChangeCaseIdRef.current) {
        send({ type: "lease-waived" });
        return;
      }
      // Offline the request can only fail, and its error would hide the
      // offline status; the connection coming back asks again.
      if (!window.navigator.onLine) {
        leaseOwedRef.current = true;
        return;
      }
      // Opening another programme moves this, and the answer is then about the
      // one left: a grant is given back and a refusal is not shown. A grant
      // nothing wants goes back too. A region active by then asks anew, since
      // this request was still out when it was entered.
      const generation = generationRef.current;
      let askAgain = false;
      let settled = (): void => undefined;
      acquiringRef.current = new Promise<void>((resolve) => {
        settled = resolve;
      });
      leaseRequestsRef.current += 1;
      try {
        const lease = await client.acquireEditLease({ programmeId, clientId });
        askAgain =
          generationRef.current !== generation ||
          !inStructuralRegionRef.current ||
          !mountedRef.current ||
          !canEditRef.current;
        if (askAgain) {
          await client.releaseEditLease({ leaseId: lease.id, clientId }).catch(() => undefined);
        } else {
          // Clears a refusal earlier in this session, which the banner and the
          // step badges both read.
          send({ type: "lease-acquired", lease: editLeasesEnabled ? lease : null });
        }
      } catch (cause) {
        askAgain = generationRef.current !== generation;
        if (!askAgain && inStructuralRegionRef.current && canEditRef.current) {
          const leaseError =
            cause instanceof Error ? cause : new Error("Unable to edit the programme structure");
          leaseOwedRef.current = isTransientFailure(leaseError);
          send({ type: "lease-refused", error: leaseError });
        }
      } finally {
        acquiringRef.current = null;
        settled();
      }
      if (askAgain && inStructuralRegionRef.current) {
        await request();
      }
    };
    await request();
  }, [client, clientId, editLeasesEnabled, send]);

  const readProgramme = useCallback(
    async (
      programmeId: string,
      changeCaseId?: string,
      rereading = false,
      discardLocal = false,
      afterConflict: Error | null = null,
    ): Promise<ReadOutcome> => {
      // No confirm() here: the router's useBlocker (EditorLayout) already
      // gated the navigation that leads to opening a different programme
      // while this one is dirty, so by the time this runs the switch is
      // already confirmed.
      // Background re-reads follow a module save or a workflow move. Over a
      // refusal with work in the buffer, only the reader may throw that away.
      if (
        rereading &&
        !discardLocal &&
        !afterConflict &&
        (REFUSED_STATUSES.has(syncRef.current.status) ||
          rebasingRef.current === readGenerationRef.current ||
          discardingRef.current === readGenerationRef.current) &&
        serializeEditorProgramme() !== baselineRef.current
      ) {
        // Held back, not failed: the refusal on the status is what stands.
        return "kept";
      }
      // An edit still in the session's window reads as saved to the blocker
      // that let this switch through; send it before the refs are dropped.
      const typing = rereading ? undefined : sendTyped();
      if (typing) {
        await typing;
      }
      const generation = readGenerationRef.current + 1;
      readGenerationRef.current = generation;
      rebasingRef.current = afterConflict ? generation : null;
      // A re-read out of a refusal is the reader asking for the stored copy,
      // so it replaces theirs; every other re-read leaves their work alone.
      const recovering = discardLocal && REFUSED_STATUSES.has(syncRef.current.status);
      discardingRef.current = recovering ? generation : null;
      // Any other read supersedes a discard still out, which then replaced nothing.
      discardedAtRef.current = recovering ? generation : discardLandedAtRef.current;
      if (!rereading) {
        generationRef.current += 1;
      }
      const epochAtStart = saveEpochRef.current;
      targetProgrammeIdRef.current = programmeId;
      targetChangeCaseIdRef.current = changeCaseId ?? null;
      const previousLease = syncRef.current.lease;
      // The region is a statement about the route, which this does not change, so
      // it survives, or the reacquire below has nothing to act on. A re-read leaves
      // whether this takes edits standing until it lands, and structural editing
      // open, or a gap hands the structure to whichever lead asks in it.
      // A re-read leaves the record on screen: the layout swaps a null record
      // for its loading state, which unmounted whatever asked for the re-read.
      // The open record stays with it, because the editor is still typeable
      // and every save path returns early without one.
      const openRecord = recordRef.current;
      const openChangeCase = changeCaseRef.current;
      const statusAtStart = syncRef.current.status;
      const failedSave = statusAtStart === "error";
      if (!rereading) {
        canEditRef.current = false;
        // Dropped in the tick its last edit was sent, before the lease goes
        // back: nothing can be typed into it, or saved without the lease.
        recordRef.current = null;
        changeCaseRef.current = null;
        baselineRef.current = "";
        setRecord(null);
        send({ type: "lease-released" });
        if (editLeasesEnabled && previousLease) {
          await client
            .releaseEditLease({
              leaseId: previousLease.id,
              clientId,
            })
            .catch(() => undefined);
        }
        if (readGenerationRef.current !== generation) {
          return "kept";
        }
      }
      remoteDuringReadRef.current = rereading ? [] : null;
      send({ type: "read-started", rereading, discardLocal });

      try {
        const changeCaseWorkspace = changeCaseId
          ? await client.loadChangeCaseWorkspace(programmeId, changeCaseId)
          : null;
        const loaded = changeCaseWorkspace?.programme ?? (await client.loadProgramme(programmeId));
        if (readGenerationRef.current !== generation) {
          return "kept";
        }
        // Landed: from here the read settles in this tick, before any lease
        // wait, so typing after it is sent rather than held.
        rebasingRef.current = null;
        discardingRef.current = null;
        if (!loaded) {
          throw new Error("The selected programme is not available for this account");
        }
        // No lease here: it is taken on entering the structural steps. Taking
        // one on open meant a lead with the tab open locked every builder out
        // of the module workspace. Whether the account may edit at all is
        // answered by the read rather than by a refused lease.
        const readOnly =
          !EDITABLE_WORKFLOW_STATES.has(loaded.workflowState) ||
          (!changeCaseWorkspace && !loaded.canEdit);
        // Whatever this client holds by now. Not awaited: the read's result lands
        // first, and a later read or close is not overwritten by this one.
        if (readOnly) {
          const heldLease = syncRef.current.lease;
          send({ type: "lease-released" });
          if (editLeasesEnabled && heldLease) {
            client.releaseEditLease({ leaseId: heldLease.id, clientId }).catch(() => undefined);
          }
        }

        // A save that started or settled during the read is newer than it:
        // the refs it wrote, and the status it reported, stand.
        if (rereading && !readOnly && saveEpochRef.current !== epochAtStart) {
          send({ type: "read-overtaken" });
          // A save the discard held back was dropped, not queued, and the
          // landed save means nothing is discarded after all.
          if (recovering && serializeEditorProgramme() !== baselineRef.current) {
            noteEdited();
          }
          if (inStructuralRegionRef.current) {
            await acquireStructuralLease();
          }
          return "overtaken";
        }
        if (recovering) {
          discardLandedAtRef.current = generation;
        }
        // What the reader typed while the re-read was in flight outranks the
        // read: the editor never stopped taking input, and the save path
        // merges the modules storage holds. With no record open there was
        // nothing to type into, and the store still holds the last programme.
        // A peer change mirrored meanwhile moved the baseline with it, so the
        // reader's work is measured from there. Their save sends the whole
        // document, so under their typing the read's value for it stands.
        const remote = remoteDuringReadRef.current ?? [];
        const landedOn = baselineRef.current;
        const typedMeanwhile =
          rereading &&
          !readOnly &&
          !recovering &&
          openRecord !== null &&
          serializeEditorProgramme() !== landedOn;
        if (!typedMeanwhile) {
          const migrated = migrateProgramme(loaded.structured);
          applyImportedProgramme(migrated);
          remote.forEach((recipe) => writeProgramme(recipe));
          // A re-read of the same programme keeps the reader's place in it.
          const kept = (id: string | null, items?: readonly { id: string }[]) =>
            rereading && items?.some((item) => item.id === id) ? id : (items?.[0]?.id ?? null);
          const editor = useEditorStore.getState();
          useEditorStore.setState({
            stepIndex: rereading ? editor.stepIndex : 0,
            selectedVersionId: kept(editor.selectedVersionId, editor.programme.versions),
            selectedModuleId: kept(editor.selectedModuleId, editor.programme.modules),
          });
        }
        // The fields the reader did not touch take the read's values. Where
        // both changed one field, the open revision stays, so the next save is
        // refused rather than writing over the peer.
        const rebased = typedMeanwhile
          ? rebaseTypedWork(landedOn, migrateProgramme(loaded.structured), state.programme)
          : null;
        if (rebased && Object.keys(rebased.taken).length > 0) {
          writeProgramme(rebased.taken);
        }
        // Content kept means bases kept: adopting the re-read's own module
        // hashes tells the next save this copy is current, so the stale module
        // bodies beside what was typed are written as edits over whatever the
        // re-read had just brought in.
        const keptBases =
          typedMeanwhile && openRecord
            ? {
                ...loaded,
                moduleBaseHashes: openRecord.moduleBaseHashes,
                saveRevision: rebased === null ? openRecord.saveRevision : loaded.saveRevision,
              }
            : loaded;
        recordRef.current = readOnly ? null : keptBases;
        canEditRef.current = !readOnly;
        changeCaseRef.current = readOnly ? null : (changeCaseWorkspace?.changeCase ?? null);
        baselineRef.current = typedMeanwhile
          ? (rebased?.baseline ?? landedOn)
          : serializeEditorProgramme();
        setRecord(metadataFor(loaded, changeCaseWorkspace?.changeCase));
        // A refused save's work rebased onto the read, or a failed save's, is
        // resent just below; other work kept over the read stays as unsent. A
        // refusal decides even under a failed save's status, which it leaves.
        const resend = typedMeanwhile && (afterConflict ? rebased !== null : failedSave);
        send({
          type: "read-landed",
          readOnly,
          // Both sides changed one field: the conflict is the reader's to settle.
          conflict: afterConflict && typedMeanwhile && rebased === null ? afterConflict : null,
          keptUnsent: typedMeanwhile && !resend,
        });
        // Nothing else is queued to send what the failed save left behind.
        if (resend) {
          noteEdited();
        }
        // Opening straight onto a structural step enters the region before this
        // load has run, and that attempt returned without a lease because it
        // could not yet know the caller may edit. Nothing else would retry it.
        if (inStructuralRegionRef.current) {
          await acquireStructuralLease();
        }
        return typedMeanwhile ? "kept" : "read";
      } catch (cause) {
        if (readGenerationRef.current !== generation) {
          return "kept";
        }
        rebasingRef.current = null;
        discardingRef.current = null;
        // Nothing replaced the reader's copy, so a save carrying it reports.
        discardedAtRef.current = discardLandedAtRef.current;
        const loadError =
          cause instanceof Error ? cause : new Error("Unable to open the selected programme");
        // As on success: a save that started or settled during the read wrote
        // newer refs and a newer status than the snapshot, and they stand.
        if (rereading && openRecord && saveEpochRef.current !== epochAtStart) {
          // The save's status stands; the read it overtook still never landed,
          // and that save's answer may predate what the read would have brought.
          send({
            type: "read-failed",
            error: loadError,
            conflict: null,
            restore: syncRef.current.status,
          });
          // As on success: nothing else sends what the discard held back.
          if (recovering && serializeEditorProgramme() !== baselineRef.current) {
            noteEdited();
          }
          if (inStructuralRegionRef.current) {
            await acquireStructuralLease();
          }
          return "failed";
        }
        // A re-read that never lands leaves the editor as it was rather than
        // holding a record nothing can save against.
        // Its baseline is left alone: only a mirrored peer change moved it.
        if (rereading && openRecord) {
          recordRef.current = openRecord;
          changeCaseRef.current = openChangeCase;
        }
        // A background re-read over saved work leaves it saved: reporting the
        // read's failure as a save status claims unsaved changes nobody made.
        // It shows as `behind` instead, beside the status.
        // A read-only open holds no record, and nothing typed there is kept.
        const stillSaved =
          rereading &&
          (statusAtStart === "read-only" ||
            (openRecord !== null &&
              !hasUnsavedEditorChanges(statusAtStart) &&
              serializeEditorProgramme() === baselineRef.current));
        send({
          type: "read-failed",
          error: loadError,
          conflict: afterConflict,
          restore: stillSaved ? statusAtStart : null,
        });
        // A read that never landed has not changed who may edit the structure.
        if (rereading && openRecord && inStructuralRegionRef.current) {
          await acquireStructuralLease();
        }
        return "failed";
      } finally {
        // Every way out, overtaken and failed included; a newer read owns its own.
        if (readGenerationRef.current === generation) {
          remoteDuringReadRef.current = null;
        }
      }
    },
    [acquireStructuralLease, client, clientId, editLeasesEnabled, noteEdited, send, sendTyped],
  );

  // Created on first ask and kept; dropped when another programme opens, by
  // which point leaving this one has asked about anything unsent.
  const moduleTargetsRef = useRef(new Map<string, ModuleEditTarget>());

  const openProgramme = useCallback(
    async (programmeId: string, changeCaseId?: string): Promise<void> => {
      const opened = moduleTargetKey(programmeId, changeCaseId, "");
      moduleTargetsRef.current.forEach((_target, key) => {
        if (!key.startsWith(opened)) {
          moduleTargetsRef.current.delete(key);
        }
      });
      await readProgramme(programmeId, changeCaseId);
    },
    [readProgramme],
  );

  /**
   * Save whatever is pending, and wait for it.
   *
   * Releasing the lease with a debounced save still in flight loses that edit:
   * the timer is cleared, the lease goes, and nothing ever writes it. Typing in
   * Programme structure and clicking straight to Outcomes is exactly that sequence.
   */
  const flushPendingSave = useCallback(async (): Promise<void> => {
    if (!recordRef.current) {
      return;
    }
    // Fired, not just saved: a window left armed would send later as typing.
    fireEdit();
    await flushRef.current?.().catch(() => undefined);
  }, [fireEdit]);

  const settlePendingSave = useCallback(async (): Promise<boolean> => {
    if (!recordRef.current || !holdsUnsavedWork(syncRef.current)) {
      return true;
    }
    if (!SETTLEABLE_STATUSES.has(syncRef.current.status)) {
      return false;
    }
    await flushPendingSave();
    return serializeEditorProgramme() === baselineRef.current;
  }, [flushPendingSave]);

  const enterStructuralRegion = useCallback(
    async (active: boolean): Promise<void> => {
      if (inStructuralRegionRef.current === active) {
        return;
      }
      inStructuralRegionRef.current = active;

      if (!active) {
        const held = syncRef.current.lease;
        // Flush while the lease is still held. Clearing it first makes the
        // save arrive without the lease it needs and be refused. With leases
        // off there is none to hold, and the edit still leaves with the step.
        if (held || !editLeasesEnabled) {
          await flushPendingSave();
        }
        send({ type: "lease-released" });
        if (held) {
          let landed = (): void => undefined;
          acquiringRef.current = new Promise<void>((resolve) => {
            landed = resolve;
          });
          await client.releaseEditLease({ leaseId: held.id, clientId }).catch(() => undefined);
          acquiringRef.current = null;
          landed();
        }
        // Re-entering while that was in flight returned without acquiring, the
        // release being the request out, so the region would stay active holding
        // nothing and the whole step shell, navigation included, go inert.
        if (inStructuralRegionRef.current) {
          await acquireStructuralLease();
        }
        return;
      }

      await acquireStructuralLease();
    },
    [acquireStructuralLease, client, clientId, editLeasesEnabled, flushPendingSave, send],
  );

  const closeProgramme = useCallback(async (): Promise<void> => {
    // No confirm() here either, for the same reason as openProgramme: the
    // router's blocker already gated the navigation away from the editor.
    // An edit in the session's window, or sent but still in the save's own
    // debounce, still goes, as on a switch.
    const typing = sendTyped();
    if (typing) {
      await typing;
    }
    generationRef.current += 1;
    readGenerationRef.current += 1;
    targetProgrammeIdRef.current = null;
    targetChangeCaseIdRef.current = null;
    inStructuralRegionRef.current = false;
    canEditRef.current = false;
    const lease = syncRef.current.lease;
    recordRef.current = null;
    changeCaseRef.current = null;
    baselineRef.current = "";
    setRecord(null);
    send({ type: "closed" });
    if (lease) {
      await client.releaseEditLease({ leaseId: lease.id, clientId }).catch(() => undefined);
    }
  }, [client, clientId, send, sendTyped]);

  useLayoutEffect(() => {
    rebaseAfterConflictRef.current = (conflict) => {
      const programmeId = targetProgrammeIdRef.current;
      if (programmeId) {
        readProgramme(
          programmeId,
          targetChangeCaseIdRef.current ?? undefined,
          true,
          false,
          conflict,
        ).catch(() => undefined);
      }
    };
  }, [readProgramme]);

  const reloadProgramme = useCallback(
    async ({ discardLocal = false }: { discardLocal?: boolean } = {}): Promise<void> => {
      send({ type: "reload-requested" });
      // A save sent before a peer's write can settle during the read, and its
      // answer hides that write; the read is asked again rather than dropped.
      const reread = async (): Promise<ReloadOutcome> => {
        const programmeId = targetProgrammeIdRef.current;
        if (!programmeId) {
          return "kept";
        }
        const outcome = await readProgramme(
          programmeId,
          targetChangeCaseIdRef.current ?? undefined,
          true,
          discardLocal,
        );
        if (outcome !== "overtaken") {
          return outcome;
        }
        // Only over a save that landed: a read over a failed one resends it,
        // and a failure stays the reader's to retry.
        const landed = !hasUnsavedEditorChanges(syncRef.current.status);
        return landed && syncRef.current.overtakenRereads < OVERTAKEN_REREADS ? reread() : "kept";
      };
      await reread();
    },
    [readProgramme, send],
  );

  const refreshProgrammeMetadata = useCallback(async (): Promise<void> => {
    const programmeId = targetProgrammeIdRef.current;
    if (!programmeId || !recordRef.current) {
      return;
    }
    // Guarded by generation as well as programme id: reopening the same
    // programme under another change case bumps the generation, and a late
    // response from before the switch must not put the old case's refs back.
    const generation = generationRef.current;
    const changeCaseId = targetChangeCaseIdRef.current;
    const workspace = changeCaseId
      ? await client.loadChangeCaseWorkspace(programmeId, changeCaseId)
      : null;
    const loaded = workspace?.programme ?? (await client.loadProgramme(programmeId));
    const current = recordRef.current;
    if (
      !loaded ||
      targetProgrammeIdRef.current !== programmeId ||
      generationRef.current !== generation
    ) {
      return;
    }
    recordRef.current = {
      ...loaded,
      // The content stays local, so its module bases stay with it. Fresh
      // hashes over a stale draft make the next save's per-module gate pass
      // over exactly the edits it exists to catch. With no record there is no
      // draft either, so the ones just read are the right ones.
      moduleBaseHashes: current?.moduleBaseHashes ?? loaded.moduleBaseHashes,
      structured: cloneEditorProgramme(),
    };
    changeCaseRef.current = workspace?.changeCase ?? changeCaseRef.current;
    setRecord(metadataFor(loaded, changeCaseRef.current));
  }, [client]);

  useLayoutEffect(() => {
    requestOwedLeaseRef.current = () => {
      if (!leaseOwedRef.current) {
        return;
      }
      leaseOwedRef.current = false;
      if (inStructuralRegionRef.current && !syncRef.current.holdsStructuralLease) {
        acquireStructuralLease().catch(() => undefined);
      }
    };
  }, [acquireStructuralLease]);

  const retrySave = useCallback((): void => {
    const programmeId = targetProgrammeIdRef.current;
    if (!recordRef.current || !programmeId) {
      return;
    }
    const { status: retriedStatus, error: retriedError } = syncRef.current;
    send({ type: "retried", online: window.navigator.onLine });
    // A state conflict reads `error` only off a lease request refused because
    // the programme takes no edits, which neither asking again nor a save can
    // change: the read that says so answers it, and asks again if it now does.
    if (retriedStatus === "error" && retriedError && isStateConflict(retriedError)) {
      readProgramme(programmeId, targetChangeCaseIdRef.current ?? undefined, true).catch(
        () => undefined,
      );
      return;
    }
    // A failed lease request offers Retry too, and inside the structural steps
    // the save needs the lease, so ask for it again first.
    const wantsLease = inStructuralRegionRef.current && !syncRef.current.holdsStructuralLease;
    const leased = wantsLease ? acquireStructuralLease() : Promise.resolve();
    // Re-base before resending: a retry carrying the stale revision fails
    // identically, as does every attempt after it. The refresh keeps the local
    // content and takes only the revision.
    leased
      .then(() => refreshProgrammeMetadata())
      .catch(() => undefined)
      .then(() => noteEdited())
      .catch(() => undefined);
  }, [acquireStructuralLease, noteEdited, readProgramme, refreshProgrammeMetadata, send]);

  const edit = useCallback(
    (recipe: (draft: Programme) => void, { save = "debounced" }: EditOptions = {}): void => {
      writeProgramme(recipe);
      if (save === "now") {
        fireEdit();
        return;
      }
      if (editTimerRef.current) {
        clearTimeout(editTimerRef.current);
      }
      editTimerRef.current = setTimeout(fireEdit, editWindowMs);
    },
    [editWindowMs, fireEdit],
  );

  const applyRemote = useCallback((recipe: (draft: Programme) => void): void => {
    const settled =
      recordRef.current !== null &&
      editTimerRef.current === null &&
      !syncRef.current.saveInFlight &&
      serializeEditorProgramme() === baselineRef.current;
    // Recorded only while the store is still what the save sent.
    const untouched =
      syncRef.current.saveInFlight &&
      editTimerRef.current === null &&
      serializeEditorProgramme() === untouchedRef.current;
    writeProgramme(recipe);
    if (settled) {
      baselineRef.current = serializeEditorProgramme();
      remoteDuringReadRef.current?.push(recipe);
    } else if (untouched) {
      remoteDuringSaveRef.current?.push(recipe);
      untouchedRef.current = serializeEditorProgramme();
    } else {
      remoteDuringSaveRef.current = null;
    }
  }, []);

  // A window that undid itself is dropped; behind a save still out, the
  // settle's drain sends the revert after it.
  const leave = useCallback(async (): Promise<boolean> => {
    if (holdsUnsentEdits()) {
      fireEdit();
    } else {
      disarmEdit();
    }
    const settled = await Promise.all([
      settlePendingSave(),
      ...[...moduleTargetsRef.current.values()].map((target) => target.leave()),
    ]);
    return settled.every(Boolean);
  }, [disarmEdit, fireEdit, holdsUnsentEdits, settlePendingSave]);

  const moduleTarget = useCallback((key: string): ModuleEditTarget => {
    let target = moduleTargetsRef.current.get(key);
    if (!target) {
      target = new ModuleEditTarget();
      moduleTargetsRef.current.set(key, target);
    }
    return target;
  }, []);

  const discardModuleDrafts = useCallback((): void => {
    moduleTargetsRef.current.forEach((target) => target.discard());
    moduleTargetsRef.current.clear();
  }, []);

  const session = useMemo<EditSession>(
    () => ({
      edit,
      applyRemote,
      leave,
      moduleTarget,
      discardModuleDrafts,
      get pending() {
        // An edit inside the window is unsent: it changed the store.
        return (
          holdsUnsavedWork(syncRef.current) ||
          holdsUnsentEdits() ||
          [...moduleTargetsRef.current.values()].some((target) => target.pending)
        );
      },
    }),
    [applyRemote, discardModuleDrafts, edit, holdsUnsentEdits, leave, moduleTarget],
  );

  return useMemo(
    () => ({
      record,
      status,
      error,
      behind,
      unsaved,
      session,
      openProgramme,
      retrySave,
      reloadProgramme,
      refreshProgrammeMetadata,
      closeProgramme,
      settlePendingSave,
      enterStructuralRegion,
      holdsStructuralLease,
      clientId,
    }),
    [
      behind,
      clientId,
      closeProgramme,
      enterStructuralRegion,
      error,
      holdsStructuralLease,
      openProgramme,
      record,
      refreshProgrammeMetadata,
      reloadProgramme,
      retrySave,
      session,
      settlePendingSave,
      status,
      unsaved,
    ],
  );
}

export function EditorProgrammeSyncBoundary({ value, children }: EditorProgrammeSyncBoundaryProps) {
  return (
    <EditorProgrammeSyncContext.Provider value={value}>
      {children}
    </EditorProgrammeSyncContext.Provider>
  );
}

export function useEditorProgrammeSync(): EditorProgrammeSyncValue {
  const value = useContext(EditorProgrammeSyncContext);
  if (!value) {
    throw new Error("useEditorProgrammeSync must be used within an EditorProgrammeSyncBoundary");
  }
  return value;
}

export function useOptionalEditorProgrammeSync(): EditorProgrammeSyncValue | null {
  return useContext(EditorProgrammeSyncContext);
}

/**
 * Outside an open editor, as in a section rendered alone, an edit only writes
 * the buffer: there is no programme open to send it to.
 */
const STORE_SESSION: EditSession = {
  edit: (recipe) => {
    writeProgramme(recipe);
  },
  applyRemote: (recipe) => writeProgramme(recipe),
  pending: false,
  leave: async () => true,
  // Nothing outlives the editor here, so each editor gets a target of its own.
  moduleTarget: () => new ModuleEditTarget(),
  discardModuleDrafts: () => undefined,
};

/** The edit session for the open programme. */
export function useEditSession(): EditSession {
  return useContext(EditorProgrammeSyncContext)?.session ?? STORE_SESSION;
}
